import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useState
} from "react";
import {
	EditPowerBiTile,
} from "client/Features/Workspace/LeftSliders/EditTile/EditPowerBiTile";
import {
	EditShortcutTile,
} from "client/Features/Workspace/LeftSliders/EditTile/EditShortcutTile";
import {
	AddTilesResponseModel,
	AddTileTargetModel,
	AddressBookDetailsTileModel,
	AfasDetailsTileModel,
	AutotaskDetailsTileModel,
	AzureDetailsTileModel,
	CalendarAppDetailsTileModel,
	ConnectWiseDetailsTileModel,
	DetailsTileModel,
	DocumentsAppDetailsTileModel,
	EmailAppDetailsTileModel,
	EmbedAppDetailsTileModel,
	GenericDetailsTileModel,
	GoogleAppDetailsTileModel,
	HubAppDetailsTileModel,
	IntegrationAppDetailsTileModel,
	LiquitDetailsTileModel,
	MagisterAppDetailsTileModel,
	NedapOnsDetailsTileModel,
	PowerBiDetailsTileModel,
	RssDetailsTileModel,
	SharePointDetailsTileModel,
	ShortcutDetailsTileModel,
	TeamsDetailsTileModel,
	ToDoAppDetailsTileModel,
	TopDeskDetailsTileModel,
	VilansKickAppDetailsTileModel,
	YammerDetailsTileModel,
	YouTubeDetailsTileModel,
	ZaaksysteemDetailsTileModel,
	ZenyaDocDetailsTileModel,
	ZermeloDetailsTileModel
} from "client/Api/Workspace/workspace";
import { AppType } from "client/Api/Apps/common";
import {
	ExistingTile,
	NewTile,
	TileEditMode,
} from "./common";
import { EditAfasTile } from "./EditAfasTile";
import { EditGenericTile } from "./EditGenericTile";
import { EditZermeloTile } from "./EditZermeloTile";
import { EditYouTubeTile } from "./EditYouTubeTile";
import { EditRssTile } from "./EditRssTile/EditRssTile";
import { EditCalendarTile } from "./EditCalendarTile/EditCalendarTile";
import { EditLiquitTile } from "./EditLiquitTile";
import { EditZenyaDocTile } from "./EditZenyaDocTile/EditZenyaDocTile";
import { EditZaaksysteemTile } from "./EditZaaksysteemTile/EditZaaksysteemTile";
import { EditNedapOnsTile } from "./EditNedapOnsTile/EditNedapOnsTile";
import { EditDocumentsTile } from "./EditDocumentsTile/EditDocumentsTile";
import { EditYammerTile } from "./EditYammerTile/EditYammerTile";
import { EditEmailTile } from "./EditEmailTile/EditEmailTile";
import { EditVilansKickTile } from "./EditVilansKickTile";
import { EditAutotaskTile } from "./EditAutotaskTile";
import { EditConnectWiseTile } from "./EditConnectWiseTile";
import { EditGoogleTile } from "./EditGoogleTile";
import { EditSharePointTile } from "./EditSharePointTile";
import { EditTeamsTile } from "./EditTeamsTile";
import { EditTopDeskTile } from "./EditTopDeskTile";
import { EditHubTile } from "client/Features/Workspace/LeftSliders/EditTile/EditHubTile/EditHubTile";
import { EditToDoTile } from "./EditToDoTile/EditToDoTile";
import { EditAzureTile } from "client/Features/Workspace/LeftSliders/EditTile/EditAzureTile";
import { EditEmbedAppTile } from "./EditEmbedAppTile";
import { EditMagisterTile } from "./EditMagisterTile/EditMagisterTile";
import { EditIntegrationTile } from "./EditIntegrationTile";
import { EditAddressBookTile } from "./EditAddressBookTile";

interface GeneralProps {
	ref: Ref<EditTileHandle>;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
}

export type EditTileHandle = {
	createHandler?: (target: AddTileTargetModel) => void;
	updateHandler?: () => void;
};

export type EditTileProps = {
	model: DetailsTileModel;
	mode: TileEditMode;
	onAfterSave: (response?: AddTilesResponseModel) => void;
	onIsBusyChanged(isBusy: boolean): void;
};

export const EditTile = forwardRef(
	(props: EditTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			model,
			mode,
			onAfterSave,
			onIsBusyChanged,
		} = props;

		const [isBusy, setIsBusy] = useState<boolean>(false);

		const isLoadingChangeHandler = useCallback(
			(value: boolean) => {
				setIsBusy(value);
				onIsBusyChanged(value);
			},
			[onIsBusyChanged]
		);

		const generalProps: GeneralProps = {
			ref,
			onIsLoadingChange: isLoadingChangeHandler,
			isLoading: isBusy,
			onAfterSave,
		};

		const renderUpdateTile = (updateMode: ExistingTile): ReactElement => {
			switch (model.appType) {
				case AppType.Afas:
					return (
						<EditAfasTile
							{...generalProps}
							initialModel={model as AfasDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.AddressBook:
					return (
						<EditAddressBookTile
							{...generalProps}
							initialModel={model as AddressBookDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Autotask:
					return (
						<EditAutotaskTile
							{...generalProps}
							initialModel={model as AutotaskDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.AzureApp:
					return (
						<EditAzureTile
							{...generalProps}
							initialModel={model as AzureDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Office365Calendar:
					return (
						<EditCalendarTile
							{...generalProps}
							initialModel={model as CalendarAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.ConnectWise:
					return (
						<EditConnectWiseTile
							{...generalProps}
							initialModel={model as ConnectWiseDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Documents:
					return (
						<EditDocumentsTile
							{...generalProps}
							initialModel={model as DocumentsAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Email:
					return (
						<EditEmailTile
							{...generalProps}
							initialModel={model as EmailAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Embed:
					return (
						<EditEmbedAppTile
							{...generalProps}
							initialModel={model as EmbedAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Google:
					return (
						<EditGoogleTile
							{...generalProps}
							initialModel={model as GoogleAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Hub:
					return (
						<EditHubTile
							{...generalProps}
							initialModel={model as HubAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Liquit:
					return (
						<EditLiquitTile
							{...generalProps}
							initialModel={model as LiquitDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Magister:
					return (
						<EditMagisterTile
							{...generalProps}
							initialModel={model as MagisterAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.NedapOns:
					return (
						<EditNedapOnsTile
							{...generalProps}
							initialModel={model as NedapOnsDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.PowerBi:
					return (
						<EditPowerBiTile
							{...generalProps}
							initialModel={model as PowerBiDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Rss:
					return (
						<EditRssTile
							{...generalProps}
							initialModel={model as RssDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.SharePoint:
					return (
						<EditSharePointTile
							{...generalProps}
							initialModel={model as SharePointDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Teams:
					return (
						<EditTeamsTile
							{...generalProps}
							initialModel={model as TeamsDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.ToDo:
					return (
						<EditToDoTile
							{...generalProps}
							initialModel={model as ToDoAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.TopDesk:
					return (
						<EditTopDeskTile
							{...generalProps}
							initialModel={model as TopDeskDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Url:
					return (
						<EditShortcutTile
							{...generalProps}
							initialModel={model as ShortcutDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.VilansKick:
					return (
						<EditVilansKickTile
							{...generalProps}
							initialModel={model as VilansKickAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.YouTube:
					return (
						<EditYouTubeTile
							{...generalProps}
							initialModel={model as YouTubeDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Yammer:
					return (
						<EditYammerTile
							{...generalProps}
							initialModel={model as YammerDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Zaaksysteem:
					return (
						<EditZaaksysteemTile
							{...generalProps}
							initialModel={model as ZaaksysteemDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.ZenyaDoc:
					return (
						<EditZenyaDocTile
							{...generalProps}
							initialModel={model as ZenyaDocDetailsTileModel}
							mode={updateMode}
							/>
					);
				case AppType.Zermelo:
					return (
						<EditZermeloTile
							{...generalProps}
							initialModel={model as ZermeloDetailsTileModel}
							mode={updateMode}
						/>
					);
				case AppType.Integration:
					return (
						<EditIntegrationTile
							{...generalProps}
							initialModel={model as IntegrationAppDetailsTileModel}
							mode={updateMode}
						/>
					);
				default:
					return (
						<EditGenericTile
							{...generalProps}
							initialModel={model as GenericDetailsTileModel}
							mode={updateMode}
						/>
					);
			}
		};

		const renderCreateTile = (createMode: NewTile) => {
			switch (model.appType) {
				case AppType.PowerBi:
					return (
						<EditPowerBiTile
							{...generalProps}
							initialModel={model as PowerBiDetailsTileModel}
							mode={createMode}
						/>
					);
				case AppType.Rss:
					return (
						<EditRssTile
							{...generalProps}
							initialModel={model as RssDetailsTileModel}
							mode={createMode}
						/>
					);
				case AppType.Url:
					return (
						<EditShortcutTile
							{...generalProps}
							initialModel={model as ShortcutDetailsTileModel}
							mode={createMode}
						/>
					);
				case AppType.YouTube:
					return (
						<EditYouTubeTile
							{...generalProps}
							initialModel={model as YouTubeDetailsTileModel}
							mode={createMode}
						/>
					);
				default:
					throw Error(
						`The ${model.name} tile doesn't support create mode`
					);
			}
		};

		return (
			<>
				{mode.kind === "create"
					? renderCreateTile(mode)
					: renderUpdateTile(mode)
				}
			</>
		);
	}
);
EditTile.displayName = "EditTile";
